/**
 * Hybrid Pagination Handler
 * Handles "Load More" button and page navigation using Section Rendering API
 * Maintains SEO-friendly URLs while providing smooth UX
 */

class PaginationHybrid extends HTMLElement {
  constructor() {
    super();
    this.loadMoreButton = this.querySelector('[data-load-more-button]');
    this.pageLinks = this.querySelectorAll('[data-page-link]');
    this.currentPage = parseInt(this.dataset.currentPage || '1', 10);
    this.totalPages = parseInt(this.dataset.totalPages || '1', 10);
    this.itemsPerPage = parseInt(this.dataset.itemsPerPage || '48', 10);
    this.totalItems = parseInt(this.dataset.totalItems || '0', 10);
    this.isLoading = false;
    
    if (this.loadMoreButton) {
      this.loadMoreButton.addEventListener('click', this.handleLoadMore.bind(this));
    }
    
    // Handle page number clicks (for direct navigation)
    this.pageLinks.forEach(link => {
      link.addEventListener('click', this.handlePageClick.bind(this));
    });
    
    // Initialize pagination state from URL
    this.initFromURL();
    
    // Check if button should be visible on initial load
    this.checkLoadMoreVisibility();

    // Deep-link hydration: load pages 1..N when landing on ?page=N
    if (this.dataset.needsHydration === 'true') {
      this.hydrateDeepLink();
    }
  }
  
  initFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    const pageParam = urlParams.get('page');
    const isHydrating =
      this.dataset.needsHydration === 'true' ||
      document.getElementById('ProductGridContainer')?.dataset.hydrating === 'true';

    if (pageParam) {
      const page = parseInt(pageParam, 10);
      if (page > 1 && page <= this.totalPages) {
        this.currentPage = page;
        if (!isHydrating) {
          this.updateProgress();
          this.checkLoadMoreVisibility();
        }
      }
    }
  }

  /**
   * Hydrate deep-link visits (?page=N): replace page-N-only grid with pages 1..N
   */
  async hydrateDeepLink() {
    const targetPage = this.currentPage;
    if (targetPage <= 1 || this.isLoading) return;

    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer) {
      productGridContainer.dataset.hydrating = 'true';
    }

    this.isLoading = true;
    this.setLoadingState(true);

    try {
      await this.loadPage(1, false);

      let pagination = document.querySelector('pagination-hybrid');
      if (!pagination) return;

      for (let page = 2; page <= targetPage; page++) {
        await pagination.loadPage(page, true);
        pagination.currentPage = page;
      }

      pagination.updateProgress();
      pagination.checkLoadMoreVisibility();
      pagination.scrollToPageStart(targetPage);
      document.dispatchEvent(new CustomEvent('productGridUpdated'));
    } catch (error) {
      console.error('Pagination: Error hydrating deep link:', error);
      const pagination = document.querySelector('pagination-hybrid');
      if (pagination) pagination.showError();
    } finally {
      if (productGridContainer) {
        delete productGridContainer.dataset.hydrating;
      }
      const pagination = document.querySelector('pagination-hybrid');
      if (pagination) {
        pagination.isLoading = false;
        pagination.setLoadingState(false);
      }
    }
  }

  /**
   * Scroll to the first product on a given page
   */
  scrollToPageStart(page) {
    const root =
      this.closest('#ProductGridContainer') || document.getElementById('ProductGridContainer');
    const anchor =
      root?.querySelector(`[data-page-start="${page}"]`) ||
      document.getElementById(`collection-page-${page}`);

    if (anchor) {
      anchor.scrollIntoView({ behavior: 'instant', block: 'start' });
    }
  }
  
  /**
   * Handle "Load More" button click
   * Loads next page via Section Rendering API and appends products
   */
  async handleLoadMore(event) {
    event.preventDefault();
    
    if (this.isLoading || !this.loadMoreButton) return;
    if (document.getElementById('ProductGridContainer')?.dataset.hydrating === 'true') return;
    if (this.currentPage >= this.totalPages) return;
    
    const nextPage = this.currentPage + 1;
    this.setLoadingState(true);
    
    try {
      await this.loadNextPage(nextPage);
      this.currentPage = nextPage;
      this.updateURL(nextPage);
      this.updateProgress();
      this.checkLoadMoreVisibility();
      this.updatePageNumbers(nextPage);
    } catch (error) {
      console.error('Pagination: Error loading next page:', error);
      this.showError();
    } finally {
      this.setLoadingState(false);
    }
  }
  
  /**
   * Handle page number click (direct navigation)
   * Uses Section Rendering API to replace content (not append)
   */
  async handlePageClick(event) {
    event.preventDefault();
    
    if (this.isLoading) return;
    
    const link = event.currentTarget;
    const url = new URL(link.href);
    const pageParam = url.searchParams.get('page');
    
    if (!pageParam) return;
    
    const targetPage = parseInt(pageParam, 10);
    if (targetPage === this.currentPage || targetPage < 1 || targetPage > this.totalPages) {
      return;
    }
    
    this.setLoadingState(true);
    
    try {
      // For page number clicks, replace content (not append)
      await this.loadPage(targetPage, false);
      this.currentPage = targetPage;
      this.updateURL(targetPage);
      this.updateProgress();
      this.checkLoadMoreVisibility();
      
      // Scroll to top of product grid (nearest grid when multiple sections use ProductGridContainer)
      const root = this.closest('#ProductGridContainer') || document.getElementById('ProductGridContainer');
      const productGrid = root?.querySelector('#collection-grid') || document.getElementById('collection-grid');
      if (productGrid) {
        productGrid.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } catch (error) {
      console.error('Pagination: Error loading page:', error);
      this.showError();
    } finally {
      this.setLoadingState(false);
    }
  }
  
  /**
   * Load next page via Section Rendering API
   * @param {number} page - Page number to load
   * @param {boolean} append - Whether to append products (true) or replace (false)
   */
  async loadNextPage(page) {
    return this.loadPage(page, true);
  }
  
  /**
   * Load a specific page via Section Rendering API
   * @param {number} page - Page number to load
   * @param {boolean} append - Whether to append products (true) or replace (false)
   */
  async loadPage(page, append = true) {
    const productGridContainer =
      this.closest('#ProductGridContainer') || document.getElementById('ProductGridContainer');
    if (!productGridContainer) {
      throw new Error('ProductGridContainer not found');
    }
    
    const sectionId = productGridContainer.dataset.id;
    if (!sectionId) {
      throw new Error('Section ID not found');
    }
    
    // Build URL with current filters, sort, and page parameter
    const currentUrl = new URL(window.location);
    const searchParams = new URLSearchParams(currentUrl.search);
    searchParams.set('page', page.toString());
    searchParams.set('section_id', sectionId);
    
    // Preserve search query if on search page
    const searchQuery = currentUrl.searchParams.get('q');
    if (searchQuery && !searchParams.has('q')) {
      searchParams.set('q', searchQuery);
      searchParams.set('options[prefix]', 'last');
    }
    
    const url = `${currentUrl.pathname}?${searchParams.toString()}`;
    
    // Add loading class to product grid
    const collectionGrid = productGridContainer.querySelector('.collection');
    if (collectionGrid) {
      collectionGrid.classList.add('loading');
    }
    
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const html = await response.text();
      const parsedHTML = parseHTML(html);
      const newContainer = parsedHTML.getElementById('ProductGridContainer');
      
      if (!newContainer) {
        throw new Error('ProductGridContainer not found in response');
      }
      
      if (append) {
        // Append new products to existing grid
        const newGrid = newContainer.querySelector('.collection-grid');
        const currentGrid = productGridContainer.querySelector('.collection-grid');
        
        if (newGrid && currentGrid) {
          // Extract only the new product items (excluding headers, etc.)
          const newItems = Array.from(newGrid.querySelectorAll('.grid__item'));
          newItems.forEach(item => {
            currentGrid.appendChild(item);
          });
        }
        
        // Update pagination component
        const newPagination = parsedHTML.querySelector('[data-pagination-hybrid]');
        if (newPagination) {
          this.updatePaginationData(newPagination);
          // Update the pagination UI in place (progress, button state)
          const newProgressText = newPagination.querySelector('.pagination-hybrid__progress-text');
          const currentProgressText = this.querySelector('.pagination-hybrid__progress-text');
          if (newProgressText && currentProgressText) {
            currentProgressText.innerHTML = newProgressText.innerHTML;
          }
          
          const newProgressBar = newPagination.querySelector('.pagination-hybrid__progress-bar-fill');
          const currentProgressBar = this.querySelector('.pagination-hybrid__progress-bar-fill');
          if (newProgressBar && currentProgressBar) {
            currentProgressBar.style.width = newProgressBar.style.width;
          }
          
          // Update load more button
          const newLoadMore = newPagination.querySelector('[data-load-more-button]');
          
          if (newLoadMore) {
            // Still more pages - update button
            const clonedButton = newLoadMore.cloneNode(true);
            if (this.loadMoreButton) {
              this.loadMoreButton.replaceWith(clonedButton);
            } else {
              this.appendChild(clonedButton);
            }
            this.loadMoreButton = this.querySelector('[data-load-more-button]');
            if (this.loadMoreButton) {
              this.loadMoreButton.addEventListener('click', this.handleLoadMore.bind(this));
              this.loadMoreButton.hidden = false;
            }
          } else {
            // No more pages - hide button
            if (this.loadMoreButton) {
              this.loadMoreButton.hidden = true;
            }
          }
        }
      } else {
        // Replace entire content (for page number navigation)
        productGridContainer.innerHTML = newContainer.innerHTML;
        
        // Update pagination component
        const newPagination = parsedHTML.querySelector('[data-pagination-hybrid]');
        if (newPagination) {
          // Clone and replace - custom element will auto-initialize when added to DOM
          const clonedPagination = newPagination.cloneNode(true);
          this.replaceWith(clonedPagination);
          
          // Ensure custom element is upgraded if not already
          if (clonedPagination instanceof PaginationHybrid === false) {
            // Force upgrade if needed
            customElements.upgrade(clonedPagination);
          }
        }
      }
    } finally {
      // Remove loading class
      if (collectionGrid) {
        collectionGrid.classList.remove('loading');
      }
    }
  }
  
  /**
   * Update pagination data attributes from new pagination element
   */
  updatePaginationData(newPaginationElement) {
    if (newPaginationElement.dataset.currentPage) {
      this.dataset.currentPage = newPaginationElement.dataset.currentPage;
      this.currentPage = parseInt(newPaginationElement.dataset.currentPage, 10);
    }
    if (newPaginationElement.dataset.totalPages) {
      this.dataset.totalPages = newPaginationElement.dataset.totalPages;
      this.totalPages = parseInt(newPaginationElement.dataset.totalPages, 10);
    }
    if (newPaginationElement.dataset.totalItems) {
      this.dataset.totalItems = newPaginationElement.dataset.totalItems;
      this.totalItems = parseInt(newPaginationElement.dataset.totalItems, 10);
    }
  }
  
  /**
   * Update URL with page parameter for SEO
   */
  updateURL(page) {
    const currentUrl = new URL(window.location);
    if (page === 1) {
      currentUrl.searchParams.delete('page');
    } else {
      currentUrl.searchParams.set('page', page.toString());
    }
    
    // Update URL without page reload
    window.history.pushState({ page }, '', currentUrl.toString());
  }
  
  /**
   * Update progress indicator
   */
  updateProgress() {
    const itemsShown = Math.min(this.currentPage * this.itemsPerPage, this.totalItems);
    const progressPercent = (itemsShown / this.totalItems) * 100;
    
    const currentSpan = this.querySelector('.pagination-hybrid__progress-current');
    if (currentSpan) {
      currentSpan.textContent = itemsShown;
    }
    
    const progressBar = this.querySelector('.pagination-hybrid__progress-bar-fill');
    if (progressBar) {
      progressBar.style.width = `${progressPercent}%`;
    }
  }
  
  /**
   * Check if Load More button should be visible
   */
  checkLoadMoreVisibility() {
    if (!this.loadMoreButton) return;
    
    if (this.currentPage >= this.totalPages) {
      this.loadMoreButton.hidden = true;
    } else {
      this.loadMoreButton.hidden = false;
    }
  }
  
  /**
   * Update page numbers visibility and state
   */
  updatePageNumbers(newPage) {
    // Show page numbers after 3 pages or if on page 4+
    const pageNumbers = this.querySelector('[data-page-numbers]');
    if (pageNumbers && (newPage >= 4 || this.totalPages <= 7)) {
      pageNumbers.hidden = false;
    }
    
    // Update active page indicator
    const pageLinks = this.querySelectorAll('.pagination-hybrid__page-link');
    pageLinks.forEach(link => {
      link.classList.remove('pagination-hybrid__page-link--current');
      if (link.textContent.trim() === newPage.toString()) {
        link.classList.add('pagination-hybrid__page-link--current');
      }
    });
  }
  
  /**
   * Set loading state for button
   */
  setLoadingState(isLoading) {
    this.isLoading = isLoading;
    
    if (!this.loadMoreButton) return;
    
    const text = this.loadMoreButton.querySelector('.pagination-hybrid__load-more-text');
    const spinner = this.loadMoreButton.querySelector('.pagination-hybrid__load-more-spinner');
    
    if (isLoading) {
      this.loadMoreButton.disabled = true;
      if (text) text.style.display = 'none';
      if (spinner) spinner.style.display = 'inline-flex';
      this.loadMoreButton.classList.add('pagination-hybrid__load-more--loading');
    } else {
      this.loadMoreButton.disabled = false;
      if (text) text.style.display = '';
      if (spinner) spinner.style.display = 'none';
      this.loadMoreButton.classList.remove('pagination-hybrid__load-more--loading');
    }
  }
  
  /**
   * Show error message
   */
  showError() {
    const errorMsg = document.createElement('div');
    errorMsg.className = 'pagination-hybrid__error';
    errorMsg.textContent = 'Unable to load more products. Please try again.';
    errorMsg.style.padding = 'var(--m, 16px)';
    errorMsg.style.textAlign = 'center';
    errorMsg.style.color = '#eb5757';
    
    this.appendChild(errorMsg);
    
    setTimeout(() => {
      errorMsg.remove();
    }, 5000);
  }
}

// Register custom element
customElements.define('pagination-hybrid', PaginationHybrid);

// Handle browser back/forward navigation
window.addEventListener('popstate', (event) => {
  const pagination = document.querySelector('pagination-hybrid');
  if (pagination) {
    pagination.initFromURL();
  }
});
